import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { createTamu, getTamuFormFields } from '../../Api';

/** ACF choices arrive as { value: label } (or a list when the keys are 0..n). */
const choicesOf = (field) => Object.entries(field.choices || {});

/** Labels shown here for the stored Tipe Group values, in display order. */
const GROUP_LABELS = { GROOM: 'Pihak Pria', BRIDE: 'Pihak Wanita' };

/**
 * Scanner-side tweaks to the ACF fields. Only labels, order and the Family option
 * change; the values saved stay the ones invitation.serenityrsvp.id uses.
 */
function adaptFields(list) {
  return list.map((field) => {
    if (field.name === 'tipe_group') {
      const entries = choicesOf(field);
      const known = Object.keys(GROUP_LABELS)
        .map((v) => entries.find(([value]) => value.toUpperCase() === v))
        .filter(Boolean)
        .map(([value]) => [value, GROUP_LABELS[value.toUpperCase()]]);
      const rest = entries.filter(([value]) => !GROUP_LABELS[value.toUpperCase()]);
      return { ...field, choices: Object.fromEntries([...known, ...rest]) };
    }

    if (field.name === 'berlaku_untuk') {
      // Family is replaced by the free "N Orang" input next to the fixed options.
      const choices = Object.fromEntries(choicesOf(field).filter(([value]) => value !== 'Family'));
      const defaultValue = field.default_value === 'Family' ? '' : field.default_value;
      return { ...field, choices, default_value: defaultValue };
    }

    return field;
  });
}

const isEmpty = (v) => v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0);

/** Starting value for a field, matching what acf_form() pre-selects. */
function initialValue(field) {
  const def = field.default_value;
  const first = choicesOf(field)[0]?.[0] ?? '';

  switch (field.type) {
    case 'checkbox':
      if (Array.isArray(def)) return def.map(String);
      return isEmpty(def) ? [] : [String(def)];
    case 'true_false':
      return Boolean(Number(def));
    case 'select':
      if (field.multiple) return Array.isArray(def) ? def.map(String) : isEmpty(def) ? [] : [String(def)];
      if (!isEmpty(def)) return String(Array.isArray(def) ? def[0] : def);
      return field.allow_null ? '' : first;
    case 'radio':
    case 'button_group':
      if (!isEmpty(def)) return String(def);
      return field.allow_null ? '' : first;
    default:
      return def ?? '';
  }
}

/** ACF conditional logic: OR across groups, AND within a group. */
function isVisible(field, fields, values) {
  const logic = field.conditional_logic;
  if (!Array.isArray(logic) || logic.length === 0) return true;

  const valueOf = (key) => {
    const target = fields.find((f) => f.key === key);
    return target ? values[target.name] : undefined;
  };

  return logic.some((group) =>
    group.every(({ field: key, operator, value }) => {
      const current = valueOf(key);
      const list = Array.isArray(current) ? current.map(String) : [String(current === true ? 1 : current === false ? 0 : current ?? '')];

      switch (operator) {
        case '==': return list.includes(String(value));
        case '!=': return !list.includes(String(value));
        case '==empty': return isEmpty(current) || current === false;
        case '!=empty': return !(isEmpty(current) || current === false);
        case '==contains': return list.some((v) => v.includes(String(value)));
        case '!==contains': return !list.some((v) => v.includes(String(value)));
        default: return true;
      }
    })
  );
}

/** Seats on the invitation, read the way the server does (fairytale_format_tamu). */
function seatsFor(berlaku) {
  if (berlaku === 'Family') return 5;
  return parseInt(berlaku, 10) || 1;
}

function Stepper({ label, value, disabled, onChange }) {
  return (
    <div className="tambah-tamu__stepper">
      <span className="tambah-tamu__stepper-label">{label}</span>
      <div className="tambah-tamu__stepper-ctrl">
        <button type="button" aria-label="Kurangi" disabled={disabled || value <= 0} onClick={() => onChange(value - 1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" /></svg>
        </button>
        <span className="tambah-tamu__stepper-value">{value}</span>
        <button type="button" aria-label="Tambah" disabled={disabled} onClick={() => onChange(value + 1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>
      </div>
    </div>
  );
}

/** Berlaku Untuk: the fixed options plus a free count, saved as "N Orang". */
function BerlakuInput({ field, value, disabled, onChange }) {
  const choices = choicesOf(field);
  const isChoice = choices.some(([v]) => v === value);
  const [custom, setCustom] = useState(!isChoice && !isEmpty(value));
  const name = `tambah-tamu-${field.name}`;
  const count = custom ? parseInt(value, 10) || '' : '';

  const pickCustom = () => {
    if (custom) return;
    setCustom(true);
    onChange('');
  };

  return (
    <div className="tambah-tamu__choices is-row">
      {choices.map(([v, label]) => (
        <label key={v} className="tambah-tamu__choice">
          <input
            type="radio"
            name={name}
            checked={!custom && value === v}
            disabled={disabled}
            onChange={() => {
              setCustom(false);
              onChange(v);
            }}
          />
          <span>{label}</span>
        </label>
      ))}

      <label className="tambah-tamu__choice">
        <input type="radio" name={name} checked={custom} disabled={disabled} onChange={pickCustom} />
        <input
          type="number"
          className="tambah-tamu__count"
          min="1"
          inputMode="numeric"
          placeholder="Jumlah"
          aria-label="Jumlah tamu"
          value={count}
          disabled={disabled}
          onFocus={pickCustom}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange(n > 0 ? `${n} Orang` : '');
          }}
        />
        <span>Orang</span>
      </label>
    </div>
  );
}

function FieldInput({ field, value, disabled, onChange }) {
  if (field.name === 'berlaku_untuk' && ['radio', 'button_group', 'select'].includes(field.type)) {
    return <BerlakuInput field={field} value={value} disabled={disabled} onChange={onChange} />;
  }

  const id = `tambah-tamu-${field.name}`;

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          id={id}
          rows={field.rows || 4}
          placeholder={field.placeholder || ''}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'select':
      return (
        <select
          id={id}
          multiple={Boolean(field.multiple)}
          value={value}
          disabled={disabled}
          onChange={(e) =>
            onChange(field.multiple ? [...e.target.selectedOptions].map((o) => o.value) : e.target.value)
          }
        >
          {field.allow_null && !field.multiple && <option value="">- Pilih -</option>}
          {choicesOf(field).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      );

    case 'radio':
    case 'button_group':
      return (
        <div className={`tambah-tamu__choices ${field.layout === 'horizontal' || field.type === 'button_group' ? 'is-row' : ''}`}>
          {choicesOf(field).map(([v, label]) => (
            <label key={v} className="tambah-tamu__choice">
              <input
                type="radio"
                name={id}
                value={v}
                checked={value === v}
                disabled={disabled}
                onChange={() => onChange(v)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className={`tambah-tamu__choices ${field.layout === 'horizontal' ? 'is-row' : ''}`}>
          {choicesOf(field).map(([v, label]) => (
            <label key={v} className="tambah-tamu__choice">
              <input
                type="checkbox"
                value={v}
                checked={value.includes(v)}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked ? [...value, v] : value.filter((x) => x !== v))}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      );

    case 'true_false':
      return (
        <label className="tambah-tamu__choice">
          <input type="checkbox" checked={value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
          <span>{field.message || (value ? field.ui_on_text || 'Ya' : field.ui_off_text || 'Tidak')}</span>
        </label>
      );

    default:
      return (
        <input
          id={id}
          type={['number', 'email', 'url'].includes(field.type) ? field.type : 'text'}
          min={field.min}
          max={field.max}
          step={field.step}
          placeholder={field.placeholder || ''}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

/**
 * Usher-only "Tambah Tamu" popup. The inputs come from ACF field group 69 on the
 * server, the group the Input Data Tamu form on invitation.serenityrsvp.id uses,
 * so both forms stay the same without a copy of the fields here. The QR code and
 * the check-in fields are left to the scanner; "Tamu sudah hadir" covers check-in.
 */
function TambahTamu({ customer, onCreated }) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(null);
  const [title, setTitle] = useState('');
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [hadir, setHadir] = useState(false);
  const [pax, setPax] = useState(1);
  const [kids, setKids] = useState(0);
  const [gift, setGift] = useState(false);

  // Bugis invitations have no gift toggle at check-in.
  const isBugis = localStorage.getItem('temp_cat') === 'Bugis';

  const reset = (list) => {
    setTitle('');
    setValues(Object.fromEntries(list.map((f) => [f.name, initialValue(f)])));
    setHadir(false);
    setKids(0);
    setGift(false);
  };

  const toggleHadir = (checked) => {
    // Start from the seats on the invitation, like the scanner does.
    if (checked) setPax(seatsFor(values.berlaku_untuk));
    setHadir(checked);
  };

  const openForm = async () => {
    setOpen(true);
    if (fields) {
      reset(fields);
      return;
    }

    try {
      setLoading(true);
      setLoadError('');
      const { fields: raw } = await getTamuFormFields();
      const list = adaptFields(raw || []);
      setFields(list);
      reset(list);
    } catch (err) {
      console.error('Load tamu form error:', err);
      setLoadError('Gagal memuat form tamu.');
    } finally {
      setLoading(false);
    }
  };

  const visibleFields = (fields || []).filter((f) => isVisible(f, fields, values));

  const save = async (e) => {
    e.preventDefault();

    const missing = [
      !title.trim() && 'Nama Tamu',
      ...visibleFields.filter((f) => f.required && isEmpty(values[f.name])).map((f) => f.label),
    ].filter(Boolean);

    if (missing.length) {
      Swal.fire({ icon: 'warning', title: 'Belum lengkap', text: `${missing.join(', ')} wajib diisi.` });
      return;
    }

    // Like acf_form(), fields hidden by conditional logic are not saved.
    const payload = Object.fromEntries(visibleFields.map((f) => [f.name, values[f.name]]));

    try {
      setSaving(true);
      const checkin = hadir ? { pax, kids, bawa_amplop: gift && !isBugis ? 'bawa' : 'tidak' } : null;
      await createTamu(customer, title.trim(), payload, checkin);
      setOpen(false);
      Swal.fire({
        icon: 'success',
        title: 'Data tamu tersimpan',
        text: hadir ? 'Tamu langsung tercatat hadir.' : 'Tamu belum hadir. Check-in lewat scan atau pencarian di scanner.',
        timer: 2200,
        showConfirmButton: false,
      });
      if (hadir && onCreated) onCreated();
    } catch (err) {
      console.error('Create tamu error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Gagal menyimpan data tamu.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button type="button" className="tamu-notes-btn" onClick={openForm}>
        + Tambah Tamu
      </button>

      {open && (
        <div className="guest-modal" role="dialog" aria-modal="true">
          <div className="guest-modal__scrim" onClick={() => !saving && setOpen(false)} />
          <form className="guest-modal__box guest-modal__box--wide tambah-tamu" onSubmit={save} noValidate>
            <div className="tambah-tamu__body">
              <h4 className="guest-modal__name">Input Data Tamu</h4>
              <p className="guest-modal__meta">
                Tamu baru masuk ke daftar tamu undangan. Centang Tamu sudah hadir untuk langsung check-in.
              </p>

              {loading && <p className="tamu-msg">Memuat form . . .</p>}
              {!loading && loadError && <p className="tamu-msg tamu-msg--error">{loadError}</p>}

              {!loading && !loadError && fields && (
                <div className="tambah-tamu__fields">
                  <div className="tambah-tamu__field">
                    <label htmlFor="tambah-tamu-title">
                      Nama Tamu <span className="tambah-tamu__req">*</span>
                    </label>
                    <input
                      id="tambah-tamu-title"
                      type="text"
                      value={title}
                      disabled={saving}
                      autoFocus
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  {visibleFields.map((field) => (
                    <div key={field.key} className="tambah-tamu__field">
                      <label htmlFor={`tambah-tamu-${field.name}`}>
                        {field.label}
                        {Boolean(field.required) && <span className="tambah-tamu__req"> *</span>}
                      </label>
                      {field.instructions && <p className="tambah-tamu__hint">{field.instructions}</p>}
                      <FieldInput
                        field={field}
                        value={values[field.name]}
                        disabled={saving}
                        onChange={(v) => setValues((prev) => ({ ...prev, [field.name]: v }))}
                      />
                    </div>
                  ))}

                  <div className="tambah-tamu__checkin">
                    <label className="guest-modal__toggle">
                      <input type="checkbox" checked={hadir} disabled={saving} onChange={(e) => toggleHadir(e.target.checked)} />
                      <span>Tamu sudah hadir (langsung check-in)</span>
                    </label>

                    {hadir && (
                      <>
                        <div className="tambah-tamu__steppers">
                          <Stepper label="Dewasa" value={pax} disabled={saving} onChange={setPax} />
                          <Stepper label="Anak" value={kids} disabled={saving} onChange={setKids} />
                        </div>

                        {!isBugis && (
                          <label className="guest-modal__toggle">
                            <input type="checkbox" checked={gift} disabled={saving} onChange={(e) => setGift(e.target.checked)} />
                            <span>Bawa Amplop / Gift</span>
                          </label>
                        )}

                        {pax + kids === 0 && (
                          <p className="guest-modal__hint">0 Dewasa dan 0 Anak tercatat sebagai titipan undangan.</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="guest-modal__actions">
              <button type="button" className="guest-modal__cancel" disabled={saving} onClick={() => setOpen(false)}>
                Batal
              </button>
              <button type="submit" className="guest-modal__save" disabled={saving || loading || !fields}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default TambahTamu;
